import {
  Group,
  MultiSelect,
  NumberInput,
  Select,
  SelectItem,
  Stack,
  TextInput,
  Checkbox,
} from '@mantine/core'
import { useForm, isNotEmpty } from '@mantine/form'
import { useDispatch, useSelector } from 'react-redux'
import React, { forwardRef, useState, useEffect, useRef, useImperativeHandle } from 'react'
import { EntityParams } from 'app/models/entity-params'
import {
  Option,
  currencyOptions,
  lenderEligibilityCriteriaOptions,
  pikOptions,
  staticFacilityTypes,
  strBooleanOptions,
  classificationOptions
} from 'app/models/dropdown-options'
import moment from 'moment'
import { createOrEditBulkFacilities, loadAllFacilities, saveFacility } from 'app/state/ducks/facilities/thunks'
import CustomDatePicker from 'app/views/components/date-picker/date-picker-logic'
import { DraftFacilityParams, FacilityAmountData, FacilityParams } from 'app/models/facility-params'
import config from 'app/config/config'
import cleanUUID from 'app/views/components/functions/cleanUUID'
import { getWireInstructions } from 'app/state/ducks/wire-instructions/selectors'
import { DealParams } from 'app/models/deal-params'
import { getEntities } from 'app/state/ducks/entities/selectors'
import { getContacts } from 'app/state/ducks/contacts/selectors'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { ContactParams } from 'app/models/contact-params'
import { StableKey } from 'app/models/common-types'
import StepHeader from 'app/views/components/modals/step-header'
import { formatDateToUTC, stringToDate } from 'app/utils/util-functions'
import { showAmortizationSchedule } from './updated-facilities-screen'
import {
  loadNullFacilityAmendment,
  loadOneFacilityAmendment,
  saveFacilityAmendment,
} from 'app/state/ducks/facility-amendments/thunks'
import {
  getFacilityAmendment,
  getFacilityAmendments,
} from 'app/state/ducks/facility-amendments/selectors'
import {
  ErrorNotification,
  SuccessNotification,
} from 'app/views/components/notifications/notification'
import { loadFeesByFacility } from 'app/state/ducks/fees/thunks'
import { loadSchedulerAmortizationByFacility } from 'app/state/ducks/amortization/thunks'
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors'
import { HolidayCalendarParams } from 'app/models/holiday-calendar-params'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { getFacilities } from 'app/state/ducks/facilities/selectors'
import { getEntityProfiles } from 'app/state/ducks/entity-profile/selectors'
import { EntityRole } from 'app/models/entity-profile-params'
import { getFacilityFees } from 'app/state/ducks/fees/selectors'
import UploadCSV from 'app/views/components/upload-csv/upload-csv'
import { transformFacilityCSVToSubmission } from './facility-calculations/facility-transformCSV'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { FacilityAmountsForOneFacilityStatus } from 'app/state/ducks/facilities/reducer'
import {
  currentFacilityAmountDescription,
  formatFacilityAmount,
} from 'app/state/ducks/facilities/facility-amount-display'

export interface Props {
  deal?: DealParams
  facility?: DraftFacilityParams
  facilityAmounts?: FacilityAmountData[]
  facilityAmountsStatus?: FacilityAmountsForOneFacilityStatus
  setSaveButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>
  setDisabledSteps: React.Dispatch<React.SetStateAction<number[]>>
  setFacility: React.Dispatch<React.SetStateAction<any>>
  dealStepper?: boolean
  amendment?: string | null
  onClose?: () => void
}

export interface ChildFacilityRef {
  handleClick: () => void
}

const UpdatedFacilitiesModal: React.ForwardRefRenderFunction<
  ChildFacilityRef,
  Props
> = (
  {
    deal,
    facility,
    amendment,
    setSaveButtonDisabled,
    setDisabledSteps,
    setFacility,
    dealStepper,
    onClose,
    facilityAmounts,
    facilityAmountsStatus,
  },
  ref
) => {
    const entities: EntityParams[] = useSelector(getEntities)
    const entityProfiles = useSelector(getEntityProfiles)
    const contacts: ContactParams[] = useSelector(getContacts)
    const facilities = useSelector(getFacilities)
    const deals: DealParams[] = useSelector(getDeals)
    const holidayCalendars: HolidayCalendarParams[] = useSelector(getCalendars)
    const businessDate = useSelector(getBusinessDate)
    const fees = useSelector(getFacilityFees);
    const prevIsNameAutoGenerated = useRef<boolean | null>(null) // Track previous state of checkbox for auto-generating facility name
    const indexRateOptions = useSelector(getIndexRateOptions)
    const initialIsNameAutoGenerated = facility?.isNameAutoGenerated ?? true;

    const holidayCalendarsOptions = holidayCalendars?.filter(calendar => calendar.status === 'Active').map(calendar => ({ label: calendar.name, value: calendar.id ?? '' }))

    const borrowersToDisplay = (ents: EntityParams[]) => {
      return ents?.filter(entity =>
        entity.entityProfile.some(profile => entityProfiles.find(option => option.id === profile.id)?.roles.includes(EntityRole.BorrowerRole)))
        .map(entity => ({ label: entity.entityName, value: entity.id ?? '' }))
    }
    const dealBorrowersIds = deal?.borrowers.map(borrower => borrower.id) ?? []
    const borrowerEnts = entities?.filter(entity => dealBorrowersIds.includes(entity?.id ?? ''))
    const dealBorrowers = borrowersToDisplay(borrowerEnts)
    const allBorrowers = borrowersToDisplay(entities) ?? []

    const borrowersDropdownOptions: SelectItem[] = deal ? dealBorrowers : allBorrowers;
    const [borrowerDropdown, setBorrowerDropdown] = useState<SelectItem[]>(borrowersDropdownOptions)

    const dealsOptions = deals
      ? deals.map(deal => ({ label: deal.dealName, value: deal.id ?? '' }))
      : []

    const wireInstructionList: any = useSelector(getWireInstructions)
    const [borrowerWireInstructionsArray, setBorrowerWireInstructionsArray] = useState<any>(facility?.borrowers ? getWireInstructionOptions('borrower', facility?.borrowers?.map(e => e.id)) : [])
    const [adminWireInstructionsArray, setAdminWireInstructionsArray] = useState<any>(facility?.adminEntity?.id ? getWireInstructionOptions('admin', [facility?.adminEntity?.id]) : [])
    const [selectedDeal, setSelectedDeal] = useState<DealParams | undefined>(deal)
    const dispatch = useDispatch()
    const facilityAmendments: FacilityParams[] = useSelector(
      getFacilityAmendments
    )
    const amended_facility =
      facilityAmendments?.find(amendment => amendment.id === facility?.id) ?? null
    const facilityAmendment: FacilityParams = useSelector(getFacilityAmendment)

    const initDealBorrowers = deal?.borrowers.length === 1 ? deal?.borrowers?.map((b: any) => {
      return b.id
    }) : []


    const handleBorrowersChange = (e: string[]) => {
      if (e.length <= 0) {
        form.setFieldValue('borrowers', [])
        form.setFieldValue('borrowerPaymentInstructions', [])
      } else {
        form.setFieldValue('borrowers', e)
      }
      loadWireInstructionOptions('borrowers', e)
    }

    const handleAdminChange = (e: string | null) => {
      if (!e) return
      form.setFieldValue('opsAdmin', '')
      form.setFieldValue('adminEntity', e)
      if (e.length <= 0) {
        form.setFieldValue('adminAgentPaymentInstructions', [])
      }
      loadWireInstructionOptions('admin', [e])
    }

    const handleFacilityTypeChange = (e: string) => {
      form.setFieldValue('facilityType', e)
      if (e === 'TL' || e === 'TLA' || e === 'TLB' || e === 'TLC') {
        form.setFieldValue('isSublimit', 'false')
      }
      form.setFieldValue('facilityType', e)
      updateDynamicName('type', e)
    }

    const handleAmountChange = (e: number) => {
      form.setFieldValue('amount', e)
      form.validateField('amount')
      updateDynamicName('amount', e)
    }

    const handleOriginalAmountChange = (e: number) => {
      form.setFieldValue('originalAmount', e)
      // if original amount is provided, use this to set the amount in the auto-generated facility name
      updateDynamicName('originalAmount', e > 0 ? e : '');
    }

    const handleCurrencyChange = (e: string) => {
      form.setFieldValue('baseCurrency', e)
      updateDynamicName('currency', e)
    }

    const updateDynamicName = (
      key: keyof typeof form.values.dynamicNameObj,
      value: string | any
    ) => {
      const dynaobj = { ...form.values.dynamicNameObj };
      dynaobj[key] = value;
      form.setFieldValue('dynamicNameObj', dynaobj);
      if (form.values.isNameAutoGenerated) {
        const newFacilityName = generateFacilityName(dynaobj)
        if (newFacilityName !== form.values.name) {
          form.setFieldValue('name', newFacilityName)
        }
      }
    };

    const handleDealChange = (dealId: string) => {
      form.setFieldValue('dealId', dealId)
      const curDeal = deals.find(deal => deal.id == dealId)
      setSelectedDeal(curDeal)
      const dealBorrowersIds = curDeal?.borrowers.map(borrower => borrower.id) ?? []
      const borrowerEnts = entities?.filter(entity => dealBorrowersIds.includes(entity?.id ?? ''))
      const dealBorrowers = borrowersToDisplay(borrowerEnts)
      setBorrowerDropdown(dealBorrowers)
      const starterBorrowers = curDeal?.borrowers.length === 1 ? curDeal?.borrowers?.map((b: any) => {
        return b.id
      }) : []
      form.setFieldValue('borrowers', starterBorrowers)
      if (starterBorrowers.length > 0) {
        loadWireInstructionOptions('borrowers', starterBorrowers)
      }
      form.validate()
    }

    function getWireInstructionOptions(type: string, value: string[]) {
      if (value) {
        const arr: any = []
        value.map((id: string) => {
          wireInstructionList.map(
            (b: {
              id: string
              admin: string
              entity: { id: string }
              currency: string
              beneficiaryAccountNumber: string
            }) => {
              if (b.entity.id.replaceAll('"', '') == id) {
                const option: Option = {
                  label: b.currency + '_' + b.beneficiaryAccountNumber,
                  value: b.id,
                }
                arr.push(option)
              }
            }
          )
        })
        return arr
      }
      return []
    }

    function loadWireInstructionOptions(type: string, value: string[]) {
      const getWireInstructions = getWireInstructionOptions(type, value)
      if (value) {
        if (type === 'admin') {
          setAdminWireInstructionsArray(getWireInstructions)
        } else {
          setBorrowerWireInstructionsArray(getWireInstructions)
        }
      }
    }

    const generateFacilityName = (dynamicNameObj: any) => {
      // use original amount if provided, otherwise use amount (which is same as current amount form label)
      const amountToUse =
        dynamicNameObj.originalAmount && dynamicNameObj.originalAmount > 0
          ? dynamicNameObj.originalAmount
          : dynamicNameObj.amount;
      const formattedAmount =
        amountToUse > 0
          ? `${new Intl.NumberFormat('en-US', {
            style: 'decimal',
            maximumFractionDigits: 1,
            minimumFractionDigits: 1,
            notation: 'compact',
          }).format(amountToUse)} ${dynamicNameObj.currency ?? 'USD'}`
          : '';
      return `${dynamicNameObj.type ?? ''} (${moment(dynamicNameObj.date).format('YYYY-MMM').toUpperCase()}) ${formattedAmount}`;
    };

    const transformValues = (values: any) => ({
      ...values,
      agentFacilityId: values.agentFacilityId || null,
      isSublimit: values.isSublimit === 'true',
      amendmentDate: amendment ? formatDateToUTC(values.amendmentDate) : undefined,
      startDate: formatDateToUTC(values.startDate),
      maturity: formatDateToUTC(values.maturity),
      opsAdmin: contacts.find(contact => contact.id === values.opsAdmin)
        ? { id: values.opsAdmin, admin: contacts.find(contact => contact.id === values.opsAdmin)?.maker }
        : null,
      borrowers: values.borrowers.map((id: string) => ({
        id,
        admin: entities.find(entity => entity.id === id)?.maker,
      })),
      adminEntity: values.adminEntity
        ? { id: values.adminEntity, admin: entities.find(entity => entity.id === values.adminEntity)?.maker }
        : null,
      borrowerPaymentInstructions: values.borrowerPaymentInstructions.map((id: string) => {
        const instruction = wireInstructionList.find((wire: StableKey) => wire.id === id);
        return { id, admin: instruction?.public };
      }),
      adminAgentPaymentInstructions: values.adminAgentPaymentInstructions.map((id: string) => {
        const instruction = wireInstructionList.find((wire: StableKey) => wire.id === id);
        return { id, admin: instruction?.public };
      }),
      holidayCalendar: values.holidayCalendar.map((id: string) => {
        const curCal = holidayCalendars.find((hc) => hc?.id === id);
        return { id, admin: curCal?.agencyAdmin };
      }),
      baseCurrency: values.baseCurrency === '' ? null : values.baseCurrency,
      pikOption: values.pikOption === '' ? null : values.pikOption,
      pikScheduler: values.pikOption !== "PIK_Scheduled" ? null : values.pikScheduler,
      restrictLoanDate: false,
      hasError: false,
      hasSuccessfulEntitySave: false,
      ErrorMessage: '',
      customer_token: config.company.toUpperCase(),
    });

    const amendDate = () => {
      // Preserve an existing amendmentDate over the start-date fallback.
      // The previous order silently re-bound amendmentDate to facility.startDate
      // in edit mode, which let the user lower both fields together and produce
      // an amendmentDate earlier than FacilityAmounts[0].effectiveDate — the
      // backdate path that triggered the `fromSome: None` crash on approve.
      // See docs/bugs/amendment-startdate-bug-chain.md.
      if (facility?.amendmentDate) {
        return stringToDate(facility.amendmentDate)
      }
      if (amendment === 'edit' && facility?.startDate) {
        return stringToDate(facility.startDate)
      }
      return businessDate
    }

    const getAmount = (amendmentDate: Date): number => {
      if (!facilityAmounts || facilityAmounts.length === 0) {
        return Number(facility?.amount ?? 0);
      }
      const currentAmounts = facilityAmounts.findLast(fa => stringToDate(fa.effectiveDate) <= amendmentDate);
      if (currentAmounts) {
        return Number(currentAmounts.amount);
      }
      return Number(facilityAmounts[0]?.amount ?? 0);
    }

    const isEditingExistingFacility = Boolean(facility?.id)

    const defaultAmendDate = amendDate()
    const form = useForm({
      initialValues: {
        id: facility?.id ?? undefined,
        transactionId: facility?.transactionId ?? undefined,
        dynamicNameObj: {
          type: facility?.facilityType ?? '',
          amount: facility?.amount ?? '',
          originalAmount: facility?.originalAmount ?? '',
          date: facility?.startDate ? stringToDate(facility?.startDate) : businessDate,
          currency: facility?.baseCurrency ?? 'USD',
        },
        name: facility?.name ?? '',
        dealId: selectedDeal ? facility?.dealId?.id ?? selectedDeal.id : facility?.dealId?.id ?? '',
        amount: amendment && facility?.status?.toLowerCase() === "draft" && facility?.amount ? Number(facility?.amount) : getAmount(defaultAmendDate),
        originalAmount: facility?.originalAmount ? Number(facility?.originalAmount) : undefined,
        utilizedAmount: facility?.utilizedAmount ?? 0,
        amortizationScheduleId: facility?.amortizationScheduleId ?? null,
        accrualFee: facility?.accrualFee ?? [],
        holidayCalendar: facility?.holidayCalendar?.map((b: any) => b.id) ?? deal?.holidayCalendar?.map((b: any) => b.id) ?? [],
        adminEntity: cleanUUID(facility?.adminEntity?.id ?? ''),
        opsAdmin: cleanUUID(facility?.opsAdmin?.id ?? ''),
        currencies: facility?.currencies ? facility.currencies : [],
        baseCurrency: facility?.baseCurrency ?? null,
        borrowers: facility?.borrowers?.map((b: any) => b.id) ?? initDealBorrowers ?? [],
        isSublimit: facility?.isSublimit == true ? 'true' : 'false',
        lenderEligibilityCriteria: facility?.lenderEligibilityCriteria ?? [],
        facilityType: facility?.facilityType ?? '',
        pikOption: facility?.pikOption ?? null,
        startDate: facility?.startDate ? stringToDate(facility?.startDate) : businessDate,
        maturity: facility?.maturity ? stringToDate(facility?.maturity) : businessDate,
        cusip: facility?.cusip,
        isin: facility?.isin,
        lxId: facility?.lxId ?? '',
        bbgId: facility?.bbgId ?? '',
        clientId: facility?.clientId ?? '',
        agentFacilityId: facility?.agentFacilityId ?? '',
        borrowerPaymentInstructions: facility?.borrowerPaymentInstructions?.map((b: any) => cleanUUID(b.id)) ?? [],
        adminAgentPaymentInstructions: facility?.adminAgentPaymentInstructions?.map((b: any) => cleanUUID(b.id)) ?? [],
        subLimits: facility?.subLimits ?? [],
        iroValues: facility?.iroValues ?? [],
        restrictLoanDate: facility?.restrictLoanDate ?? false,
        amendmentDate: defaultAmendDate,
        amendmentLenders: facility?.amendmentLenders ?? [],
        amendmentFees: facility?.amendmentFees ?? [],
        amendmentAmortization: facility?.amendmentAmortization ?? null,
        nextAmortizationPaymentData: facility?.nextAmortizationPaymentData ?? null,
        pikScheduler: facility?.pikScheduler ?? null,
        classification: facility?.classification ?? null,
        systemId: facility?.systemId ?? null,
        isNameAutoGenerated: initialIsNameAutoGenerated,
      },
      transformValues,
      validate: {
        name: isNotEmpty('Facility Name cannot be empty'),
        facilityType: isNotEmpty('Facility Type cannot be empty'),
        dealId: isNotEmpty('Deal cannot be empty'),
        amount: (value: number) => validateAvailability(value),
        originalAmount: value => (value === null || value === undefined ? null : isNaN(Number(value)) || Number(value) <= 0 ? 'Invalid amount' : null),
        lxId: value => (value == '' || value.length === 8 ? null : 'Invalid LX ID'),
        bbgId: value => (value == '' || value.length === 9 ? null : 'Invalid BBG ID'),
        classification: value => (value ? null : 'Classification is required'),
        startDate: (value, values) => {
          const facilityStartDate = formatDateToUTC(value)
          const dealStartDate = formatDateToUTC(selectedDeal?.startDate as unknown as Date)
          const valuesMaturity = formatDateToUTC(values.maturity)
          if (facilityStartDate && selectedDeal) {
            if (facilityStartDate < dealStartDate) {
              return 'Start date should not be before Deal start date.'
            }
          }
          if (facilityStartDate && valuesMaturity) {
            if (facilityStartDate > valuesMaturity) {
              return 'Start date should not be after Maturity date.'
            }
          }
          return null
        },
        maturity: (value, values) => {
          const facilityMaturity = formatDateToUTC(value)
          const dealMaturity = formatDateToUTC(selectedDeal?.maturity as unknown as Date)
          const valueStartDate = formatDateToUTC(values.startDate)
          if (facilityMaturity && selectedDeal) {
            if (facilityMaturity > dealMaturity) {
              return 'Maturity date should not be after Deal maturity date.'
            }
          }
          if (facilityMaturity && valueStartDate) {
            if (facilityMaturity < valueStartDate) {
              return 'Maturity date should not be before Start date.'
            }
          }
          return null
        }
      },
      validateInputOnBlur: true,
      validateInputOnChange: true,
    });

    const validateAvailability = (value: number | string) => {
      const notEmptyError = isNotEmpty('Amount cannot be empty')(value);
      if (notEmptyError) return notEmptyError;

      const numericValue = Number(value);
      if (isNaN(numericValue)) return 'Amount must be a valid number';

      if (selectedDeal) {
        const totalFacilityAmount = facilities
          .filter(fact => fact.dealId.id === selectedDeal.id && fact.id !== facility?.id && fact.status && fact.status === 'Approved')
          .reduce((acc, cur) => Number(acc) + Number(cur.amount), 0);
        const currentFacilityTotals = Number((totalFacilityAmount + numericValue).toFixed(2));
        const currentDealAvailability = Number(Number(selectedDeal.amount).toFixed(2));
        if (currentFacilityTotals > currentDealAvailability) {
          return 'Deal Availability Exceeded';
        }

      }

      if (numericValue < 0) {
        return 'Amount has to be greater than 0';
      }

      return null;
    };


    useEffect(() => {
      if (facility && facility?.id && amended_facility) {
        dispatch(loadOneFacilityAmendment(facility?.transactionId ?? facility?.id, 'Draft'))
      } else if (amended_facility) {
        dispatch(loadNullFacilityAmendment())
      }
      if (borrowerWireInstructionsArray.length === 0 && initDealBorrowers.length > 0) {
        loadWireInstructionOptions('borrowers', initDealBorrowers)
      }
    }, [amended_facility, facility?.id, facility])

    useEffect(() => {
      setSaveButtonDisabled(!form.isValid())
    }, [form.isValid()])

    useEffect(() => {
      const currentIsNameAutoGenerated = form.values.isNameAutoGenerated
      if (currentIsNameAutoGenerated) {
        const newFacilityName = generateFacilityName(form.values.dynamicNameObj)
        if (newFacilityName !== form.values.name) {
          form.setFieldValue('name', newFacilityName)
        }
      } else if (
        prevIsNameAutoGenerated.current === true && // Was previously checked
        currentIsNameAutoGenerated === false // Now unchecked
      ) {
        // Clear only on toggle from true to false
        form.setFieldValue('name', '');
      }
      // Update the previous value for the next render
      prevIsNameAutoGenerated.current = currentIsNameAutoGenerated;
    }, [
      form.values.isNameAutoGenerated,
      form.values.dynamicNameObj.type,
      form.values.dynamicNameObj.amount,
      form.values.dynamicNameObj.originalAmount,
      form.values.dynamicNameObj.date,
      form.values.dynamicNameObj.currency,
    ]);

    const handleClick = async () => {
      await onSubmit()
    }

    // Expose the handleClick method to the parent component
    useImperativeHandle(ref, () => ({
      handleClick,
    }))

    const onSubmit = async () => {
      try {
        const facilityValues = form.getTransformedValues();
        const deal = selectedDeal ?? deals.find(deal => deal.id === facilityValues.dealId) ?? null;

        if (!deal) {
          ErrorNotification({ title: 'Facility Submission Failed', message: 'Deal is not selected' });
          return;
        }

        const facilityparams: DraftFacilityParams = {
          ...facilityValues,
          dealId: { id: deal.id, admin: deal.accountManagementAdmin },
          subLimits: facilityValues.isSublimit === false ? [] : facility?.subLimits ?? [],
          facilityPricingGrid: facility?.facilityPricingGrid ?? [],
        };

        let response: any = null;
        if (amendment) {
          response = await dispatch(saveFacilityAmendment(facilityparams, facilityAmendment, fees));
        } else {
          response = await dispatch(saveFacility(facilityparams, !!facilityValues.id));
        }

        if (response?.success === false) {
          ErrorNotification({ title: 'Facility Submission Failed', message: response?.payload || 'Check Lender Allocations and try again' });
          return;
        }

        setFacility(response.payload);
        dispatch(loadFeesByFacility(response.payload.id));
        dispatch(loadSchedulerAmortizationByFacility(response.payload.id));

        const newDisabledSteps = facilityparams.isSublimit === false ? (dealStepper ? [5] : [2]) : [];
        const disableAmortization = showAmortizationSchedule(facilityValues) ? [] : dealStepper ? [9] : [6];
        const disablePik = facilityValues.pikOption === 'PIK_Scheduled' ? [] : dealStepper ? [10] : [7];
        const disablePricingGrid = deal.isPricingGrid ? [] : dealStepper ? [2, 8] : [5];

        setDisabledSteps(newDisabledSteps.concat(disableAmortization, disablePik, disablePricingGrid));

        SuccessNotification({ title: 'Successful Submission', message: 'Facility has been Submitted for Approval' });
      } catch {
        ErrorNotification({ title: 'Error', message: 'Please Check Inputs' });
      }
    };


    const adminAgents: SelectItem[] = entities
      ?.filter(entity => {
        if (selectedDeal && selectedDeal.adminEntity) {
          const adminEntityIds = selectedDeal.adminEntity.map(admin => admin.id);
          return adminEntityIds.includes(entity.id ?? '');
        }
        return false; // Exclude entities if selectedDeal is undefined or doesn't match
      })
      .filter(entity =>
        // Additional filter for roles
        entity.entityProfile.some(profile =>
          entityProfiles.find(option => option.id === profile.id)?.roles.includes(EntityRole.AdminAgentRole)
        )
      )
      .map((entity: EntityParams) => {
        return {
          label: entity.mei ? `${entity.entityName} - ${entity.mei}` : entity.entityName,
          value: entity.id ?? ''
        };
      });

    const handleCSVData = async (parsedData: Record<string, string>[]) => {
      const transformedFacilities = transformFacilityCSVToSubmission(parsedData, deals, facilities, indexRateOptions, entities, contacts, holidayCalendars, businessDate, transformValues)
      await dispatch(createOrEditBulkFacilities(transformedFacilities))
      handleBulkNotificationAndClosure()
    }

    async function handleBulkNotificationAndClosure() {
      SuccessNotification({
        title: 'Successfully Saved Bulk Facilities',
        message: 'You created many facilities',
      })
      dispatch(loadAllFacilities())
      if (onClose) { onClose() }
    }


    return (
      <>
        {onClose && (
          <StepHeader
            title={`${facility?.name ? 'Edit' : 'Create'} Facility`}
            onClose={onClose}
          />
        )}
        <form>
          <div style={{ position: 'relative' }}>
            <Group
              noWrap
              w="100%"
              position='right'
              style={{ position: 'absolute', top: '-32px', right: '0', zIndex: 1 }}
            >
              <UploadCSV onParse={handleCSVData} />
            </Group>
          </div>
          <Stack>
            {amendment && (
              <Group noWrap w="100%">
                <CustomDatePicker
                  label={'Amendment Date'}
                  date={form.values.amendmentDate ?? businessDate}
                  setDate={(value: any) => {
                    form.setFieldValue('amendmentDate', value)
                    const newAmount = getAmount(value);
                    form.setFieldValue('amount', newAmount);
                  }}
                  holidayCalendars={
                    deal?.holidayCalendar ?? form.values.holidayCalendar ?? []
                  }
                  // Block backdating below the facility's current startDate.
                  // Mirrors the DAML Amend / EditAmendment guard so the user
                  // hits a clear UI constraint instead of a backend assert.
                  mindate={facility?.startDate ? stringToDate(facility.startDate) : undefined}
                  disabled={amendment != null && amendment === 'edit' ? true : false}
                />
              </Group>
            )}
            <Group noWrap w="100%">
              <div style={{ width: "100%" }}>
                <Group align="center" spacing="xs">

                  <label htmlFor="dealName" style={{ fontFamily: 'Plus Jakarta Sans', fontSize: '14px', fontWeight: 500, color: '#212529', lineHeight: '1.55', marginRight: '10px' }}>
                    Facility Name
                  </label>
                  <Checkbox
                    label="Auto-Generate"
                    checked={form.values.isNameAutoGenerated}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      form.setFieldValue('isNameAutoGenerated', event.currentTarget.checked)
                    }
                  />

                </Group>
                <TextInput
                  withAsterisk
                  w="100%"
                  name="name"
                  id="name"
                  placeholder={form.values.isNameAutoGenerated
                    ? "Facility Name is Auto-Generated"
                    : "Enter Custom Facility Name"}
                  {...form.getInputProps('name')}
                  disabled={form.values.isNameAutoGenerated}
                />
              </div>
              <Select
                w="100%"
                searchable
                clearable
                label="Base Currency"
                placeholder="Select a Base Currency"
                data={currencyOptions ?? []}
                onChange={(e: string) => {
                  handleCurrencyChange(e)
                }}
                value={form.values.baseCurrency}
              />
            </Group>
            <Group noWrap w="100%">
              <Select
                w="100%"
                clearable
                searchable
                withAsterisk
                label="Facility Type"
                placeholder="Select a Facility Type"
                data={staticFacilityTypes ?? []}
                onChange={(e: string) => handleFacilityTypeChange(e)}
                value={form.values.facilityType}
              />

              <Select
                hidden={deal != undefined}
                w="100%"
                clearable
                searchable
                withAsterisk
                label="Deal"
                placeholder="Select a Deal"
                disabled={deal ? true : false}
                data={dealsOptions ?? []}
                onChange={(e: string) => handleDealChange(e)}
                value={form.values.dealId}
              />
            </Group>
            <Group noWrap w="100%">
              <TextInput
                w="100%"
                label="Client ID"
                name="clientId"
                id="clientId"
                placeholder="Enter Client ID"
                {...form.getInputProps('clientId')}
                onChange={(e) => form.setFieldValue('clientId', e.target.value.toUpperCase())}
              />
              <TextInput
                w="100%"
                label="Facility CUSIP"
                name="cusip"
                id="cusip"
                placeholder="Enter Facility CUSIP"
                minLength={9}
                maxLength={9}
                {...form.getInputProps('cusip')}
                onChange={(e) => form.setFieldValue('cusip', e.target.value.toUpperCase())}
                error={
                  !form.values.cusip || form.values.cusip.length == 9
                    ? null
                    : 'Cusip Should Be 9 Characters'
                }
              />
              <TextInput
                w="100%"
                label="Facility ISIN"
                name="isin"
                id="isin"
                placeholder="Enter Facility ISIN"
                minLength={12}
                maxLength={12}
                {...form.getInputProps('isin')}
                onChange={(e) => form.setFieldValue('isin', e.target.value.toUpperCase())}
                error={
                  !form.values.isin || form.values.isin.length == 12
                    ? null
                    : 'ISIN Should Be 12 Characters'
                }
              />

              <TextInput
                w="100%"
                label="Facility LXID"
                name="lxId"
                id="lxId"
                placeholder="Enter Facility LXID"
                minLength={8}
                maxLength={8}
                {...form.getInputProps('lxId')}
                onChange={(e) => form.setFieldValue('lxId', e.target.value.toUpperCase())}
                error={
                  !form.values.lxId || form.values.lxId.length == 8
                    ? null
                    : 'LXID Should Be 8 Characters'
                }
              />

            </Group>
            <Group noWrap w="100%">
              <TextInput
                w="100%"
                label="Facility BBG ID"
                name="bbgId"
                id="bbgId"
                placeholder="Enter Facility Bloomberg Id"
                minLength={9}
                maxLength={9}
                {...form.getInputProps('bbgId')}
                onChange={(e) => form.setFieldValue('bbgId', e.target.value.toUpperCase())}
                error={
                  form.values.bbgId && form.values.bbgId.length > 0 && form.values.bbgId.length !== 9
                    ? 'BBG ID Should Be 9 Characters'
                    : null
                }
              />
              <TextInput
                w="100%"
                label="Agent Facility ID"
                name="agentFacilityId"
                id="agentFacilityId"
                placeholder="Enter Agent Facility ID"
                {...form.getInputProps('agentFacilityId')}
              />
              <Select
                clearable
                searchable
                w="100%"
                placeholder="Select Administrative Agent"
                label="Administrative Agent"
                data={adminAgents ?? []}
                {...form.getInputProps('adminEntity')}
                onChange={e => handleAdminChange(e)}
                value={form.values.adminEntity}
              />
              <MultiSelect
                searchable
                w="100%"
                placeholder="Select Wire Instructions"
                label="Admin Agent Instructions"
                name="adminAgentPaymentInstructions"
                data={adminWireInstructionsArray ?? []}
                {...form.getInputProps('adminAgentPaymentInstructions')}
              />
            </Group>
            <Group noWrap w="100%" align="flex-end">

              <NumberInput
                w="100%"
                pb={5}
                label="Original Amount"
                id="originalAmount"
                placeholder="Enter Original Amount"
                precision={2}
                parser={value => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                formatter={value =>
                  !Number.isNaN(parseFloat(value ?? ''))
                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    : ''
                }
                {...form.getInputProps('originalAmount')}
                onChange={(e: number) => {
                  handleOriginalAmountChange(e)
                }}
              />
              <NumberInput
                w="100%"
                withAsterisk
                pb={5}
                label={
                  isEditingExistingFacility
                    ? `Facility Amount as of ${moment(form.values.amendmentDate ?? businessDate).format('DD MMM YYYY')}`
                    : 'Current Amount'
                }
                description={currentFacilityAmountDescription(
                  isEditingExistingFacility,
                  facilityAmountsStatus,
                  formatFacilityAmount(getAmount(businessDate), form.values.baseCurrency),
                )}
                id="amount"
                placeholder="Enter Current Amount"
                precision={2}
                parser={value => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                formatter={value =>
                  !Number.isNaN(parseFloat(value ?? ''))
                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    : ''
                }
                {...form.getInputProps('amount')}
                onChange={(e: number) => {
                  handleAmountChange(e)
                }}
              />
              <MultiSelect
                w="100%"
                searchable
                label="Available Currencies"
                placeholder="Select a Currencies"
                data={currencyOptions ?? []}
                {...form.getInputProps('currencies')}
              />
            </Group>
            <Group noWrap w="100%">
              <Select
                clearable
                searchable
                w="100%"
                placeholder="Select PIK"
                label="PIK Options"
                data={pikOptions ?? []}
                {...form.getInputProps('pikOption')}
              />

              <Select
                clearable
                searchable
                w="100%"
                disabled={
                  form.getInputProps('facilityType').value === 'TL' ||
                  form.getInputProps('facilityType').value === 'TLA' ||
                  form.getInputProps('facilityType').value === 'TLB' ||
                  form.getInputProps('facilityType').value === 'TLC'
                }
                placeholder="Is it Sublimits"
                label="Sublimits"
                data={strBooleanOptions}
                {...form.getInputProps('isSublimit')}
              />
            </Group>
            <Group noWrap w="100%">
              <CustomDatePicker
                label={'Start Date'}
                date={form.values.startDate}
                setDate={(value: any) => {
                  form.setFieldValue('startDate', value)
                  updateDynamicName('date', value)
                }}
                errorMsg={form.errors.startDate as any}
                holidayCalendars={
                  deal?.holidayCalendar ?? form.values.holidayCalendar ?? []
                }
                // Block setting the facility's startDate below the deal's
                // startDate. Mirrors the DAML Amend / EditAmendment guard.
                mindate={(deal?.startDate ?? selectedDeal?.startDate)
                  ? stringToDate((deal?.startDate ?? selectedDeal?.startDate) as any)
                  : undefined}
              />


              <CustomDatePicker
                label={'Maturity Date'}
                date={form.values.maturity}
                setDate={(value: any) => {
                  form.setFieldValue('maturity', value)
                }}
                errorMsg={form.errors.maturity as any}
                holidayCalendars={
                  deal?.holidayCalendar ?? form.values.holidayCalendar ?? []
                }

              />

            </Group>
            <Group noWrap w="100%">
              <MultiSelect
                searchable
                w="100%"
                placeholder="Borrowers"
                label="Borrowers"
                name="borrowers"
                data={borrowerDropdown ?? []}
                onChange={e => handleBorrowersChange(e)}
                value={form.values.borrowers}
              />

              <MultiSelect
                searchable
                w="100%"
                placeholder="Select Wire Instructions"
                label="Borrower Wire Instructions"
                name="borrowerPaymentInstructions"
                data={borrowerWireInstructionsArray ?? []}
                {...form.getInputProps('borrowerPaymentInstructions')}
              />
            </Group>
            <Select
              withAsterisk
              w="100%"
              searchable
              placeholder="Select Classification"
              label="Classification"
              data={classificationOptions ?? []}
              {...form.getInputProps('classification')}
            />
            <Group noWrap w="100%">
              <MultiSelect
                searchable
                w="100%"
                placeholder="Select Lender Eligibility Criteria"
                label="Lender Eligibility Criteria"
                name="lenderEligibilityCriteria"
                data={lenderEligibilityCriteriaOptions ?? []}
                {...form.getInputProps('lenderEligibilityCriteria')}
              />

              <Select
                clearable
                searchable
                w="100%"
                placeholder="Select Administrative Agent Contact"
                label="Administrative Agent Contact"
                data={
                  contacts && contacts.length > 0
                    ? contacts
                      .filter(
                        contact =>
                          contact.entity &&
                          contact.entity.id.replace(/['"]/g, '') ===
                          form.values.adminEntity
                      )
                      .map(contact => ({
                        value: contact.id ?? '',
                        label: contact.name,
                      }))
                    : []
                }
                {...form.getInputProps('opsAdmin')}
              />
            </Group>
            <Group noWrap w="100%">
              <MultiSelect
                w="100%"
                searchable
                label="Holiday Calendar"
                placeholder="Select a Holiday Calendar"
                data={holidayCalendarsOptions ?? []}
                {...form.getInputProps('holidayCalendar')}
              />
            </Group>
          </Stack>
        </form>
      </>
    )
  }

export default forwardRef(UpdatedFacilitiesModal)
